import {
    makeStyles,
    createStyles,
    Theme,
} from '@bb-ui/react-library/dist/components/styles';

import { illuminateColors } from '../../utilities/sharedStyles';
import { MAX_RATING, MIN_RATING } from './FeedbackWidget.types';

const PANEL_WIDTH = 320;

/** The gap between the button stack and what opens beside it. */
const BESIDE_GAP = '8px';

export const useStyles = makeStyles(
    (theme: Theme) =>
        createStyles({
            // The nudge and the panel open left of the stack, leaving the help
            // button above uncovered.
            widget: {
                position: 'relative',
                display: 'flex',
            },
            anchor: {
                position: 'relative',
                display: 'flex',
            },
            promptWrap: {
                position: 'absolute',
                right: `calc(100% + ${BESIDE_GAP})`,
                top: '50%',
                transform: 'translateY(-50%)',
                // Sized by the bubble, not by the 40px-wide stack it hangs off.
                width: 'max-content',
                // Room for the dismiss button, which overlaps the corner.
                paddingTop: theme.spacing(1),
                paddingRight: theme.spacing(1),
            },
            prompt: {
                display: 'block',
                // Fits left of the stack on a phone; 260px keeps it to two lines.
                maxWidth: 'min(260px, calc(100vw - 96px))',
                padding: theme.spacing(1, 1.5),
                border: 'none',
                borderRadius: theme.spacing(2),
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                boxShadow: theme.shadows[3],
                fontFamily: 'inherit',
                fontSize: theme.typography.fontSizeSmall,
                fontWeight: theme.typography.fontWeightSemiBold,
                lineHeight: 1.35,
                textAlign: 'left',
                cursor: 'pointer',
                // Not b2, which is the same colour as background.paper.
                '&:hover': {
                    backgroundColor: theme.palette.background.b3,
                },
                '&:focus': {
                    outline: `2px solid ${theme.palette.focus.main}`,
                    outlineOffset: '2px',
                },
            },
            promptDismiss: {
                position: 'absolute',
                top: 0,
                right: 0,
                // 24px is the WCAG 2.5.8 minimum target size.
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                borderRadius: '50%',
                border: `1px solid ${theme.palette.background.b4}`,
                backgroundColor: theme.palette.background.paper,
                color: theme.palette.text.primary,
                boxShadow: theme.shadows[2],
                cursor: 'pointer',
                '& svg': {
                    width: '12px',
                    height: '12px',
                },
                '&:hover': {
                    backgroundColor: theme.palette.background.b4,
                },
                '&:focus': {
                    outline: `2px solid ${theme.palette.focus.main}`,
                    outlineOffset: '1px',
                },
            },
            // Undoes floatingButton's spacing, which the stack puts on the widget
            // instead. Doubled to win whichever stylesheet loads last.
            launcherButton: {
                '&&': {
                    marginBottom: 0,
                    padding: 0,
                    cursor: 'pointer',
                },
            },
            // This icon fills its box, so at 24px it looks heavier than help's.
            // The launcher's tooltip wrapper, laid out as if it were the button.
            tooltipAnchor: {
                display: 'inline-flex',
            },
            launcherIcon: {
                width: '20px',
                height: '20px',
            },
            panel: {
                position: 'absolute',
                right: `calc(100% + ${BESIDE_GAP})`,
                // Grows upwards from the button.
                bottom: 0,
                // Focused on open; no ring around the whole dialog.
                outline: 'none',
                width: `${PANEL_WIDTH}px`,
                // Fits left of the stack on a phone.
                maxWidth: 'calc(100vw - 80px)',
                // Scrolls rather than outgrowing the screen below the header.
                maxHeight: 'calc(100vh - 120px)',
                overflowY: 'auto',
                padding: theme.spacing(3, 2, 2, 2),
                borderRadius: theme.spacing(1),
                backgroundColor: illuminateColors.navSurface,
                color: illuminateColors.navText,
                // Matches the sidebar's section divider.
                border: `1px solid ${theme.palette.border.main}`,
                boxShadow: theme.shadows[8],
            },
            closeButton: {
                position: 'absolute',
                top: theme.spacing(0.5),
                right: theme.spacing(0.5),
                color: illuminateColors.navText,
                '&:hover': {
                    color: illuminateColors.navTextActive,
                },
            },
            form: {
                display: 'flex',
                flexDirection: 'column',
            },
            title: {
                color: illuminateColors.navTextActive,
                fontSize: theme.typography.fontSizeMedium,
                fontWeight: theme.typography.fontWeightSemiBold,
                marginBottom: theme.spacing(2),
                paddingRight: theme.spacing(3),
            },
            ratingSection: {
                marginBottom: theme.spacing(2),
            },
            // A grid keeps the segments equal and unwrapped at any width.
            ratingGroup: {
                display: 'grid',
                gridTemplateColumns: `repeat(${MAX_RATING - MIN_RATING + 1}, 1fr)`,
                height: '40px',
                borderRadius: theme.spacing(0.75),
                border: `1px solid ${theme.palette.border.main}`,
                backgroundColor: illuminateColors.navSurfaceActive,
                overflow: 'hidden',
            },
            // The tooltip wrappers are the grid cells, so separators go here.
            ratingCell: {
                display: 'flex',
                borderLeft: `1px solid ${theme.palette.border.main}`,
                '&:first-child': {
                    borderLeft: 'none',
                },
            },
            ratingButton: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                padding: 0,
                cursor: 'pointer',
                border: 'none',
                backgroundColor: 'transparent',
                color: illuminateColors.navText,
                fontSize: theme.typography.fontSizeSmall,
                fontFamily: 'inherit',
                lineHeight: 1,
                transition: 'background-color 120ms ease, color 120ms ease',
                '@media (prefers-reduced-motion: reduce)': {
                    transition: 'none',
                },
                '&:hover:not(:disabled)': {
                    backgroundColor: illuminateColors.navSurfaceActive,
                    color: illuminateColors.navTextActive,
                },
                '&:focus': {
                    outline: `2px solid ${theme.palette.focus.light}`,
                    // Inset so the ring is not clipped by the group's overflow.
                    outlineOffset: '-2px',
                },
                '&:disabled': {
                    opacity: 0.5,
                    cursor: 'default',
                },
            },
            ratingFace: {
                width: '22px',
                height: '22px',
            },
            ratingButtonSelected: {
                backgroundColor: illuminateColors.brandGreen,
                color: illuminateColors.badgeAccentText,
                fontWeight: theme.typography.fontWeightSemiBold,
                '&:hover:not(:disabled)': {
                    backgroundColor: illuminateColors.brandGreen,
                    color: illuminateColors.badgeAccentText,
                },
                // Forced colours drop the green fill; Highlight keeps the pick
                // visible.
                '@media (forced-colors: active)': {
                    forcedColorAdjust: 'none',
                    backgroundColor: 'Highlight',
                    color: 'HighlightText',
                    '&:hover:not(:disabled)': {
                        backgroundColor: 'Highlight',
                        color: 'HighlightText',
                    },
                },
            },
            ratingLegend: {
                display: 'flex',
                justifyContent: 'space-between',
                marginTop: theme.spacing(0.75),
                color: illuminateColors.navText,
                opacity: 0.8,
            },
            comment: {
                marginBottom: theme.spacing(2),
            },
            commentCounter: {
                textAlign: 'right',
                marginRight: 0,
                color: illuminateColors.navText,
                opacity: 0.8,
            },
            error: {
                marginBottom: theme.spacing(1),
                // error.light fails 4.5:1 on this panel; error.main passes.
                color: theme.palette.error.main,
                fontSize: theme.typography.fontSizeSmall,
            },
            // PrimaryButton's own fill is the same tone as this panel.
            submitButton: {
                width: '100%',
                marginTop: theme.spacing(0.5),
                // So the disabled border does not change the size.
                border: '1px solid transparent',
                backgroundColor: `${illuminateColors.brandGreen} !important`,
                color: `${illuminateColors.badgeAccentText} !important`,
                fontWeight: theme.typography.fontWeightSemiBold,
                '&:hover': {
                    backgroundColor: `${illuminateColors.brandGreen} !important`,
                    color: `${illuminateColors.badgeAccentText} !important`,
                    filter: 'brightness(1.1)',
                },
                '&:focus': {
                    backgroundColor: `${illuminateColors.brandGreen} !important`,
                    color: `${illuminateColors.badgeAccentText} !important`,
                },
                // The fill alone is too faint against the panel; the border keeps
                // the shape visible. Also shown while sending (aria-disabled).
                '&:disabled, &[aria-disabled="true"]': {
                    backgroundColor: `${illuminateColors.navSurfaceActive} !important`,
                    borderColor: theme.palette.border.main,
                    color: `${theme.palette.text.secondary} !important`,
                },
            },
            thanks: {
                paddingRight: theme.spacing(3),
            },
            thanksText: {
                color: illuminateColors.navText,
                fontSize: theme.typography.fontSizeSmall,
            },
        }),
    {
        name: 'feedback-widget-',
    },
);
